import Breadcrumb from "components/Common/Breadcrumb"
import DeleteModal from "components/Common/DeleteModal"
import TableContainer from "components/Common/TableContainer"
import { SimpleStringValue } from "helpers/common_helper_functions"
import { getAllChequeTemplates, deleteChequeTemplate, getCustomerChequeTemplates, makeChequeTemplateGlobal } from "helpers/backendHelpers/chequeTemplate"
import React, { useCallback, useEffect, useMemo, useState } from "react"
import { Link } from "react-router-dom"
import { Button, Card, CardBody, Col, Container, Row, UncontrolledTooltip } from "reactstrap"
import ChequeTemplateEditorModal from "./ChequeTemplateEditorModal"
import TemplatePreviewModal from "./TemplatePreviewModal"

const ChequePrint = () => {
  const [allTemplates, setAllTemplates] = useState([])
  const [loading, setLoading] = useState(false)
  const [save, setSave] = useState(false)
  const [modal, setModal] = useState(false)
  const [isEdit, setIsEdit] = useState(false)
  const [editingTemplate, setEditingTemplate] = useState(null)
  const [deleteModal, setDeleteModal] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState(null)
  const [showCustomer, setShowCustomer] = useState(false)
  const [previewTemplate, setPreviewTemplate] = useState(null)
  const [sourceTemplateId, setSourceTemplateId] = useState(null)

  useEffect(() => {
    document.title = "Cheque Print | BillSaathi Admin"
    fetchAllTemplates()
  }, [save, showCustomer])

  const fetchAllTemplates = useCallback(async () => {
    try {
      setLoading(true)
      const response = showCustomer ? await getCustomerChequeTemplates() : await getAllChequeTemplates()
      setAllTemplates(response?.payload?.data || [])
    } catch (error) {
      // silently handle
    } finally {
      setLoading(false)
    }
  }, [showCustomer])

  const toggle = () => {
    if (modal) {
      setModal(false)
      setIsEdit(false)
      setEditingTemplate(null)
      setSourceTemplateId(null)
    } else {
      setModal(true)
    }
  }

  const handleAddButtonClick = () => {
    setIsEdit(false)
    setEditingTemplate(null)
    toggle()
  }

  const editButtonClick = template => {
    setIsEdit(true)
    setEditingTemplate(template)
    toggle()
  }

  const onClickDelete = template => {
    setDeleteTarget(template)
    setDeleteModal(true)
  }

  const handleDeleteTemplate = async () => {
    try {
      await deleteChequeTemplate(deleteTarget._id)
      setSave(prev => !prev)
    } catch (error) {
      // toast already shown by api_helper
    } finally {
      setDeleteModal(false)
      setDeleteTarget(null)
    }
  }

  const ownerName = owner => [owner?.firstName, owner?.lastName].filter(Boolean).join(" ") || owner?.userName || "—"

  // Edit a customer template: saving creates a new default template and leaves the customer's own untouched.
  const editCustomerTemplate = template => {
    setIsEdit(false)
    setEditingTemplate(template)
    setSourceTemplateId(template._id)
    setModal(true)
  }

  const handleMakeGlobal = async template => {
    if (!window.confirm(`Make "${template.bankName}" a default template for all users exactly as it is? The customer will no longer be able to edit or delete it.`)) return
    try {
      await makeChequeTemplateGlobal(template._id)
      setSave(prev => !prev)
    } catch (error) {
      // success/error toasts are shown by the shared api_helper post()
    }

  }

  const defaultColumns = useMemo(
    () => [
      {
        Header: "Bank Name",
        accessor: "bankName",
        disableFilters: true,
        Cell: cellProps => <SimpleStringValue {...cellProps} />,
      },
      {
        Header: "Leaf Size",
        id: "leafSize",
        accessor: row => `${row.leaf?.widthMm ?? "-"} × ${row.leaf?.heightMm ?? "-"} mm`,
        disableFilters: true,
      },
      {
        Header: "Created",
        accessor: "createdAt",
        disableFilters: true,
        Cell: ({ value }) => (value ? new Date(value).toLocaleDateString() : "—"),
      },
      {
        Header: "Actions",
        accessor: "actions",
        disableFilters: true,
        Cell: ({ row }) => {
          const item = row.original
          return (
            <div className="d-flex gap-3 justify-content-center">
              <Link to="#" className="text-success" onClick={() => editButtonClick(item)}>
                <i className="mdi mdi-pencil font-size-18" id={`edittooltip-${item._id}`} />
                <UncontrolledTooltip placement="top" target={`edittooltip-${item._id}`}>
                  Edit
                </UncontrolledTooltip>
              </Link>
              <Link to="#" className="text-danger" onClick={() => onClickDelete(item)}>
                <i className="mdi mdi-delete font-size-18" id={`deletetooltip-${item._id}`} />
                <UncontrolledTooltip placement="top" target={`deletetooltip-${item._id}`}>
                  Delete
                </UncontrolledTooltip>
              </Link>
            </div>
          )
        },
      },
    ],
    []
  )

  const customerColumns = useMemo(
    () => [
      {
        Header: "Customer",
        id: "customer",
        accessor: row => ownerName(row.createdByUserId),
        disableFilters: true,
      },
      {
        Header: "Mobile",
        id: "mobile",
        accessor: row => row.createdByUserId?.mobileNo || "—",
        disableFilters: true,
      },
      ...defaultColumns.slice(0, 3),
      {
        Header: "Actions",
        accessor: "actions",
        disableFilters: true,
        Cell: ({ row }) => {
          const item = row.original
          return (
            <div className="d-flex gap-3 justify-content-center align-items-center">
              <Link to="#" className="text-primary" onClick={() => setPreviewTemplate(item)}>
                <i className="mdi mdi-eye font-size-18" id={`viewtooltip-${item._id}`} />
                <UncontrolledTooltip placement="top" target={`viewtooltip-${item._id}`}>
                  View
                </UncontrolledTooltip>
              </Link>
              <Link to="#" className="text-success" onClick={() => editCustomerTemplate(item)}>
                <i className="mdi mdi-pencil font-size-18" id={`custedittooltip-${item._id}`} />
                <UncontrolledTooltip placement="top" target={`custedittooltip-${item._id}`}>
                  Edit &amp; save as default
                </UncontrolledTooltip>
              </Link>
              <Link to="#" className="text-info" onClick={() => handleMakeGlobal(item)}>
                <i className="mdi mdi-earth font-size-18" id={`globaltooltip-${item._id}`} />
                <UncontrolledTooltip placement="top" target={`globaltooltip-${item._id}`}>
                  Make default as is
                </UncontrolledTooltip>
              </Link>
            </div>
          )
        },
      },
    ],
    [defaultColumns]
  )

  const columns = showCustomer ? customerColumns : defaultColumns

  return (
    <React.Fragment>
      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDeleteTemplate}
        onCloseClick={() => {
          setDeleteModal(false)
          setDeleteTarget(null)
        }}
      />
      <div className="page-content">
        <Container fluid>
          <Breadcrumb title="Cheque Print" />
          <Row>
            <Col xs="12">
              <Card>
                <CardBody>
                  <TableContainer
                    columns={columns}
                    data={allTemplates}
                    isGlobalFilter={true}
                    isAddOptions={true}
                    addButtonLabel={showCustomer ? undefined : "Add Template"}
                    extraButtons={
                      <Button type="button" color="success" className="btn mb-2 me-2" onClick={() => setShowCustomer(prev => !prev)}>
                        <i className={`mdi ${showCustomer ? "mdi-file-document-outline" : "mdi-account-group"} me-1`} />
                        {showCustomer ? "Default Templates" : "Customer Templates"}
                      </Button>
                    }
                    handleAddButtonClick={handleAddButtonClick}
                    isPagination={false}
                    className="custom-header-css cheque-print-table"
                    dataFetchLoading={loading}
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>

          <TemplatePreviewModal
            template={previewTemplate}
            ownerName={previewTemplate?.createdByUserId ? ownerName(previewTemplate.createdByUserId) : ""}
            toggle={() => setPreviewTemplate(null)}
          />

          <ChequeTemplateEditorModal
            isOpen={modal}
            isEdit={isEdit}
            template={editingTemplate}
            toggle={toggle}
            onSaved={() => setSave(prev => !prev)}
            sourceTemplateId={sourceTemplateId}
          />
        </Container>
      </div>
    </React.Fragment>
  )
}

export default ChequePrint
